// PrescriptionSaveOrchestrator — the save flow as an explicit, resumable state
// machine, replacing the page's re-entrant latch choreography.
//
// Framework-free by contract: this file imports NO Vue and NO axios. The page
// constructs a reactive state object (Vue.observable) and the ports (buildSavePorts)
// and passes them in; the orchestrator treats state as a plain object and reaches
// the outside world only through ports.
//
// State machine:
//   idle → awaitingEquivalentsPrompt → awaitingPositiveId → awaitingGenericPrompt → saving
// A resume can only advance the flow (it can never re-trigger an earlier gate), so
// there is no suppression latch to strand. cancelPending() is the SINGLE place a
// pending attempt (and its keep-brand payload) is dropped.
//
// Key design decisions:
//  - Equivalents-gate applicability is entry-point-asymmetric; the page encodes it
//    in snapshot.equivalentsCheckApplies (true only for save/saveAndStay + config +
//    ids). The orchestrator only consumes the boolean, so the asymmetry stays where
//    it belongs and requestSave remains the single entry point.
//  - Gate ORDER follows evaluateSaveGates (deceased first — see saveDecisions).
//  - verifiedToken lives on the attempt, initialized from snapshot and consumed via
//    ports.form.consumePositiveIdToken() ONLY at the POST, so it survives every
//    pre-POST abort (clearing-on-abort is deferred to a follow-up).
//  - The post-save continuation is a single ports.nav.completeSave() call; the page
//    splits it into a pure decision step (decidePostSaveContinuation) + an executor.

import { Drug } from '@/models/Drug/Drug'
import { Prescription } from '@/models/Prescription'
import { EquivalentPrescription } from '@/models/EquivalentPrescription'
import { DrugSource } from '@/models/Drug/DrugProduct'
import type { SaveIntent } from './saveIntent'
import { evaluateSaveGates, selectGenericPromptOutcome, SaveGateContext } from './saveDecisions'

/**
 * The keep-brand selection the generic prompt emits. Declared structurally here so the
 * orchestrator stays free of any Vue/component import; BrandToGenericPromptModal's
 * KeepBrandSelection is assignable to it.
 */
export interface KeepBrandSelection {
    dawCode: number
    pharmacistInitials: string
    suggestedGenericPackageIds: number[]
}

/** The save POST response: a prescription plus the drug-category answer side-channel. */
export type SavedPrescription = Prescription & {
    answerSaveFailedCategoryIDs?: number[]
    answerSaveWarning?: string
}

/** Inputs the save POST needs beyond the prescription itself. */
export interface SavePayloadExtras {
    dispensingItems: unknown[]
    drugCategoryAnswers: unknown[]
    createNewRefill: boolean
    positiveIdToken: string | null
}

export interface DawOverrideLogPayload {
    brandPackageId: number | null
    brandDrugSource: DrugSource
    suggestedGenericPackageIds: number[]
    dawCode: number
    pharmacistInitials: string
}

/**
 * A pure-data snapshot of everything the orchestrator needs from the form/page for
 * one drive() pass. The page recomputes it on demand (buildSavePorts). It carries
 * no functions and no Vue reactivity.
 */
export interface SaveSnapshot {
    // gate inputs
    isNewOrRefill: boolean
    patientDeceased: boolean
    positiveIdEnabled: boolean
    positiveIdConfigLoaded: boolean
    positiveIdTargetKnown: boolean
    positiveIdGatePresent: boolean
    /** Token already held (from a prior verification / aborted save); seeds the attempt. */
    heldPositiveIdToken: string | null
    /** Entry-point-asymmetric: true only for save/saveAndStay with the config on and ids present. */
    equivalentsCheckApplies: boolean
    /** shouldRunGenericPromptPreflight() — a brand GSDD fill that may have generic equivalents. */
    genericPreflightApplies: boolean

    // equivalents-prescriptions gate inputs
    equivalentsQuery: {
        patientId: number
        packageId: number
        drugSource: DrugSource
        storeId: number
        rxNumber: number
    }

    // generic-substitution preflight inputs
    productId: number
    equivPackageId: number
    brandPackageId: number | null
    genericStoreId: number | undefined
    productNameLong: string | null
    isExistingFill: boolean
    rxId: string

    // saving-phase inputs
    /** queueItemID present without a saved rxNumber — a new Rx from an ingestion needs a lock. */
    needsIngestionLock: boolean
    ingestionQueueItemId: string | number | null
    fillDateTooOld: boolean
    showDeaAlert: boolean
    supersedeThisRx: boolean
    prescription: Prescription
    savePayloadExtras: Omit<SavePayloadExtras, 'positiveIdToken'>
}

export interface SavePorts {
    api: {
        save(prescription: Prescription, extras: SavePayloadExtras): Promise<SavedPrescription>
        getEquivalentPrescriptions(query: SaveSnapshot['equivalentsQuery']): Promise<EquivalentPrescription[]>
        getEquivalents(productId: number, packageId: number, storeId: number | undefined): Promise<Drug.EquivalentDrug[]>
        dawOverrideExists(rxId: string, brandPackageId: number): Promise<boolean>
        postDawOverrideLog(savedRx: Prescription, payload: DawOverrideLogPayload): void
        /**
         * Mark a dispense error resolved — called ONLY after a successful save, so a failed
         * save can never leave the error resolved against an unsaved Rx. Fire-and-forget:
         * the adapter surfaces its own failure and refreshes the alert list. `rxId` is the Rx
         * the resolve was requested against (captured pre-save); the adapter refreshes only while
         * the page is still on that Rx, so a supersede/finish save that swaps to a new Rx can't
         * make the refresh GET By-RxId for the wrong Rx.
         */
        resolveDispenseError(dispenseErrorId: number, rxId: string): void
    }
    locks: {
        /** Thin adapter over LockHandler.addLockWithURL — heartbeat/registry/release stay on the page. */
        acquireIngestionLock(queueItemId: string | number): Promise<void>
    }
    ui: {
        notifyDeceasedBlocked(): void
        notifyPositiveIdNotReady(reason: 'config-loading' | 'target-loading'): void
        alertFillDateTooOld(): void
        confirmDea(): Promise<boolean>
        showEquivalentsPrompt(equivalents: EquivalentPrescription[]): void
        showGenericPrompt(equivPackageId: number, productName: string | null, candidates: Drug.EquivalentDrug[]): void
        requestPositiveIdVerification(): void
        showIngestionLocked(error: unknown): void
        notifySaveError(error: unknown): void
    }
    form: {
        snapshot(): SaveSnapshot
        /** Propagate view-model values (status, DAW, dates, ...) onto the prescription + apply save-time
         *  field defaults. Side-effecting, so called ONCE per commit right before the gates run. */
        prepareForSave(): void
        /** Client-side validation gate; side-effecting (notifies on failure), so called ONCE at the POST. */
        validate(): boolean
        /** Clear the page's held positive-ID token; called ONLY at the POST. */
        consumePositiveIdToken(): void
        applyGenericSwap(generic: Drug.EquivalentDrug): void
        applyKeepBrandDaw(payload: KeepBrandSelection): void
        applySaved(saved: SavedPrescription): void
        /**
         * Convert the form to a new-Rx draft (supersede). Returns a restore that puts the
         * form back exactly as it was; the orchestrator invokes it ONLY when the save POST
         * fails, so the operator gets back the billed Rx and a retry behaves like the
         * first attempt.
         */
        applyNewRxDraft(): () => void
    }
    nav: {
        /** Post-save continuation (ingestion-complete + navigation + form reset + callbacks). The page splits this into decidePostSaveContinuation + an executor. */
        completeSave(intent: SaveIntent, saved: SavedPrescription): Promise<void>
    }
    now(): Date
}

/** The page-supplied reactive state object. The orchestrator treats it as plain data. */
export interface SaveOrchestratorState {
    saving: boolean
    awaiting: null | 'equivalents-prompt' | 'positive-id' | 'generic-prompt'
    checkingEquivalentDrugs: boolean
}

/** Mutable per-attempt bookkeeping — private to the orchestrator, never reactive. */
interface SaveAttempt {
    intent: SaveIntent
    positiveIdToken: string | null
    keepBrandPayload: KeepBrandSelection | null
    equivalentsChecked: boolean
    genericChecked: boolean
}

export class PrescriptionSaveOrchestrator {
    private attempt: SaveAttempt | null = null

    constructor(private readonly ports: SavePorts, private readonly state: SaveOrchestratorState) {}

    // ── public API ──────────────────────────────────────────────────────────

    /** Single Stage-B entry point. No-op while a save is in flight. */
    requestSave(intent: SaveIntent): void {
        if (this.state.saving) return
        const snap = this.ports.form.snapshot()
        this.attempt = {
            intent,
            positiveIdToken: snap.heldPositiveIdToken,
            keepBrandPayload: null,
            equivalentsChecked: false,
            genericChecked: false,
        }
        void this.drive(snap)
    }

    /** DiscontinueEquivalentScriptsModal "continue" (either Discontinue or Skip). */
    resumeEquivalentsHandled(): void {
        if (this.state.awaiting !== 'equivalents-prompt' || !this.attempt) return
        this.attempt.equivalentsChecked = true
        this.state.awaiting = null
        void this.drive()
    }

    resumeWithPositiveId(token: string): void {
        if (this.state.awaiting !== 'positive-id' || !this.attempt) return
        this.attempt.positiveIdToken = token
        this.state.awaiting = null
        void this.drive()
    }

    resumeKeepBrand(payload: KeepBrandSelection): void {
        if (this.state.awaiting !== 'generic-prompt' || !this.attempt) return
        this.attempt.keepBrandPayload = payload
        this.attempt.genericChecked = true
        this.ports.form.applyKeepBrandDaw(payload)
        this.state.awaiting = null
        void this.drive()
    }

    /** The page passes the chosen generic; the orchestrator applies the swap, then continues. */
    resumeUseGeneric(generic: Drug.EquivalentDrug): void {
        if (this.state.awaiting !== 'generic-prompt' || !this.attempt) return
        this.attempt.genericChecked = true
        this.ports.form.applyGenericSwap(generic)
        this.state.awaiting = null
        void this.drive()
    }

    /** The single place a pending attempt (and its keep-brand payload) is dropped. */
    cancelPending(_reason: 'equivalents-prompt' | 'positive-id' | 'generic-prompt'): void {
        this.attempt = null
        this.state.awaiting = null
    }

    // ── internals ───────────────────────────────────────────────────────────

    private buildContext(snap: SaveSnapshot): SaveGateContext {
        const attempt = this.attempt!
        return {
            isNewOrRefill: snap.isNewOrRefill,
            patientDeceased: snap.patientDeceased,
            positiveIdEnabled: snap.positiveIdEnabled,
            positiveIdConfigLoaded: snap.positiveIdConfigLoaded,
            positiveIdTargetKnown: snap.positiveIdTargetKnown,
            hasVerifiedToken: !!attempt.positiveIdToken,
            positiveIdGatePresent: snap.positiveIdGatePresent,
            equivalentsCheckPending: snap.equivalentsCheckApplies && !attempt.equivalentsChecked,
            genericPreflightPending: snap.genericPreflightApplies && !attempt.genericChecked,
        }
    }

    private async drive(snapshot?: SaveSnapshot): Promise<void> {
        if (!this.attempt) return
        const snap = snapshot ?? this.ports.form.snapshot()
        const decision = evaluateSaveGates(this.buildContext(snap))

        switch (decision.kind) {
            case 'blocked-deceased':
                this.ports.ui.notifyDeceasedBlocked()
                this.endAttempt()
                return
            case 'blocked-positiveid-config':
                this.ports.ui.notifyPositiveIdNotReady(decision.reason)
                this.endAttempt()
                return
            case 'need-equivalents-check':
                await this.runEquivalentsCheck(snap)
                return
            case 'need-positiveid':
                this.state.awaiting = 'positive-id'
                this.ports.ui.requestPositiveIdVerification()
                return
            case 'need-generic-preflight':
                await this.runGenericPreflight(snap)
                return
            case 'proceed':
                await this.runSave(snap)
                return
        }
    }

    private async runEquivalentsCheck(snap: SaveSnapshot): Promise<void> {
        if (!this.attempt) return
        this.state.checkingEquivalentDrugs = true
        // Only the API round-trip is inside the try; the downstream drive() runs after
        // the finally so an exception in the commit it triggers can't be misread as a
        // preflight failure (which would drive the save a second time).
        let equivalents: EquivalentPrescription[] | null = null
        try {
            equivalents = await this.ports.api.getEquivalentPrescriptions(snap.equivalentsQuery)
        } catch (err) {
            // Fail open — a preflight error must not block a save.
            console.error('Error checking equivalents:', err)
        } finally {
            this.state.checkingEquivalentDrugs = false
        }
        if (!this.attempt) return
        this.attempt.equivalentsChecked = true
        if (equivalents && equivalents.length > 0) {
            this.state.awaiting = 'equivalents-prompt'
            this.ports.ui.showEquivalentsPrompt(equivalents)
        } else {
            await this.drive()
        }
    }

    private async runGenericPreflight(snap: SaveSnapshot): Promise<void> {
        if (!this.attempt) return
        // Busy flag disables the Save button during the round-trip, mirroring the
        // equivalents check — otherwise the button stays live and a second click can
        // spawn a concurrent drive chain. Only the API calls are inside the try; the
        // downstream drive() runs after so a commit throw isn't misread as a preflight
        // failure (which would drive the save a second time).
        this.state.checkingEquivalentDrugs = true
        let candidates: Drug.EquivalentDrug[] | null = null
        let promptNeeded = false
        try {
            const equivalents = await this.ports.api.getEquivalents(snap.productId, snap.equivPackageId, snap.genericStoreId)
            const outcome = selectGenericPromptOutcome(equivalents, snap.equivPackageId, snap.isExistingFill)

            if (outcome.kind === 'prompt') {
                promptNeeded = true
                candidates = outcome.candidates
            } else if (outcome.kind === 'check-already-kept') {
                const kept = snap.brandPackageId != null
                    && await this.ports.api.dawOverrideExists(snap.rxId, snap.brandPackageId)
                if (!kept) {
                    promptNeeded = true
                    candidates = outcome.candidates
                }
            }
            // 'save-current-is-generic' / 'save-no-candidates' / already-kept → promptNeeded stays false.
        } catch (err) {
            // Fail open — proceed with the save.
            console.error('Generic-substitution preflight failed; proceeding with save.', { err })
        } finally {
            this.state.checkingEquivalentDrugs = false
        }
        if (!this.attempt) return

        if (promptNeeded && candidates) {
            this.state.awaiting = 'generic-prompt'
            this.ports.ui.showGenericPrompt(snap.equivPackageId, snap.productNameLong, candidates)
        } else {
            this.attempt.genericChecked = true
            await this.drive()
        }
    }

    private async runSave(snap: SaveSnapshot): Promise<void> {
        if (!this.attempt) return
        // Re-entrancy guard: a second drive chain (e.g. from a double-click that raced
        // through a preflight with no busy flag) must not issue a second POST. The
        // check-and-set below is synchronous, so only the first chain proceeds.
        if (this.state.saving) return
        const attempt = this.attempt
        this.state.saving = true
        let restoreNewRxDraft: (() => void) | null = null
        // Shared failure path for both catch layers: undo the supersede draft (pre-commit only —
        // the restore is nulled once the POST commits), fail any bill callback, notify.
        const failAttempt = (error: unknown) => {
            const restore = restoreNewRxDraft
            restoreNewRxDraft = null
            restore?.()
            if (attempt.intent.billPlanCallback) attempt.intent.billPlanCallback(null, true)
            this.ports.ui.notifySaveError(error)
        }

        // The ENTIRE commit sequence is inside try/finally: prepareForSave / the lock
        // adapter / the fill-date + DEA gates / validate all run here, so a throw from any
        // of them still resets `saving` (buttons stay usable) instead of latching it true
        // and escaping as an unhandled rejection.
        try {
            // Propagate the operator's view-model edits (status, DAW, dates, ...) onto the
            // prescription and apply save-time field defaults, before any gate.
            this.ports.form.prepareForSave()

            // A new Rx from an ingestion needs its queue-item lock before we save. A lock
            // conflict aborts the whole attempt — dropping the keep-brand payload and pending
            // intent — but does NOT consume the positive-ID token (pre-POST abort; the finally
            // clears the attempt while the page still holds the token for the next attempt).
            if (snap.needsIngestionLock && snap.ingestionQueueItemId != null) {
                try {
                    await this.ports.locks.acquireIngestionLock(snap.ingestionQueueItemId)
                } catch (error) {
                    this.ports.ui.showIngestionLocked(error)
                    return
                }
            }

            // Pre-POST aborts: the positive-ID token is NOT consumed, so it survives.
            if (snap.fillDateTooOld) {
                this.ports.ui.alertFillDateTooOld()
                return
            }
            if (snap.showDeaAlert) {
                const proceed = await this.ports.ui.confirmDea()
                if (!proceed) return
            }
            // Validation is side-effecting (notifies on failure), so it runs exactly here, once.
            if (!this.ports.form.validate()) return

            // Committing: consume the token exactly once, at the POST.
            const positiveIdToken = attempt.positiveIdToken
            this.ports.form.consumePositiveIdToken()
            if (snap.supersedeThisRx) {
                restoreNewRxDraft = this.ports.form.applyNewRxDraft()
            }

            try {
                const saved = await this.ports.api.save(snap.prescription, {
                    ...snap.savePayloadExtras,
                    positiveIdToken,
                })
                // The POST committed; a failure past this point (applySaved / the post-save
                // continuation) is not a save failure and must not roll the form back.
                restoreNewRxDraft = null
                this.ports.form.applySaved(saved)
                // Keep-brand audit rides the attempt, posted only after a successful save.
                if (attempt.keepBrandPayload) {
                    this.ports.api.postDawOverrideLog(saved, {
                        brandPackageId: saved.packageID,
                        brandDrugSource: saved.drugSource,
                        suggestedGenericPackageIds: attempt.keepBrandPayload.suggestedGenericPackageIds,
                        dawCode: attempt.keepBrandPayload.dawCode,
                        pharmacistInitials: attempt.keepBrandPayload.pharmacistInitials,
                    })
                }
                // Resolve the dispense error only now that the save has succeeded, so a save
                // failure can never leave the error resolved against an unsaved Rx. Pass the
                // pre-save rxId so the adapter refreshes the alert list against the right Rx even
                // when a supersede save has already swapped the page onto a new Rx.
                if (attempt.intent.resolveDispenseErrorId != null) {
                    this.ports.api.resolveDispenseError(attempt.intent.resolveDispenseErrorId, snap.rxId)
                }
                await this.ports.nav.completeSave(attempt.intent, saved)
            } catch (error) {
                failAttempt(error)
            }
        } catch (error) {
            // An unexpected throw in the commit sequence (a form port, the lock adapter)
            // must surface and fail any bill callback rather than escape unhandled.
            failAttempt(error)
        } finally {
            this.state.saving = false
            this.endAttempt()
        }
    }

    private endAttempt(): void {
        this.attempt = null
        this.state.awaiting = null
    }
}
